import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, MapPin, Facebook } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  const navLinks = [
    { name: 'Forside', href: '/', isRoute: true },
    { name: 'Priser', href: isHomePage ? '#priser' : '/#priser' },
    { name: 'Materialer', href: '/materialer', isRoute: true },
    { name: 'Lokationer', href: isHomePage ? '#lokationer' : '/#lokationer' },
    { name: 'Sådan handler du', href: isHomePage ? '#guide' : '/#guide' },
    { name: 'Kontakt', href: '/kontakt', isRoute: true },
  ];

  return (
    <nav className="fixed w-full z-50 transition-all duration-300 bg-zinc-900 shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between min-h-[4rem] xl:h-20 py-2 xl:py-0">
          <div className="flex justify-between items-center w-full xl:w-auto h-12 md:h-16 xl:h-20">
            <div className="flex-shrink-0 flex items-center w-32 lg:w-56">
              <Link to="/" className="absolute top-0 left-4 lg:left-8">
                <img 
                  src="https://assets.auxodev.dk/assets/8283c528-e59c-4f9d-b50b-8d919a0a0240/b674d1e6-956f-4f26-bd31-317c5bb756df.jpg" 
                  alt="Materialepladsen" 
                  className="h-20 md:h-28 lg:h-48 w-auto object-contain shadow-xl rounded-b-2xl"
                />
              </Link>
            </div>

            {/* Mobile/Tablet Menu Button */}
            <div className="xl:hidden flex items-center">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 rounded-md text-white z-50 relative"
                aria-label="Toggle menu"
              >
                {isOpen ? <X size={32} /> : <Menu size={32} />}
              </button>
            </div>
          </div>
          
          {/* Desktop Menu (XL and up) */}
          <div className="hidden xl:flex flex-row items-center gap-x-8 h-20">
            {navLinks.map((link) => {
              return link.isRoute ? (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-base font-bold uppercase tracking-wider transition-colors hover:text-[#a3844e] text-white"
                >
                  {link.name}
                </Link>
              ) : (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-base font-bold uppercase tracking-wider transition-colors hover:text-[#a3844e] text-white"
                >
                  {link.name}
                </a>
              );
            })}
            <a
              href="tel:+4520181271"
              className="flex items-center gap-2 bg-[#a3844e] text-white px-5 py-2.5 rounded-none text-base font-semibold hover:bg-[#a3844e]/90 transition-all shadow-lg hover:shadow-black/20"
            >
              <Phone size={16} />
              Ring nu
            </a>
          </div>
        </div>
      </div>

      {/* Sidebar Menu Overlay */}
      <div 
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 xl:hidden ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setIsOpen(false)}
      />

      {/* Sidebar Menu */}
      <div className={`fixed top-0 right-0 h-full w-[300px] bg-zinc-900 shadow-2xl transform transition-transform duration-300 ease-in-out xl:hidden z-40 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex flex-col h-full pt-24 px-6 pb-8">
          <div className="flex-1 space-y-2">
            {navLinks.map((link) => {
              return link.isRoute ? (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  className="block py-4 text-xl font-bold uppercase tracking-wide text-white border-b border-zinc-800 hover:text-[#a3844e] transition-colors"
                >
                  {link.name}
                </Link>
              ) : (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={() => setIsOpen(false)}
                  className="block py-4 text-xl font-bold uppercase tracking-wide text-white border-b border-zinc-800 hover:text-[#a3844e] transition-colors"
                >
                  {link.name}
                </a>
              );
            })}
          </div>
          
          <div className="space-y-4 pt-8">
            <a
              href="tel:+4520181271"
              className="flex items-center justify-center gap-3 w-full bg-[#a3844e] text-white px-4 py-4 rounded-none font-bold text-lg shadow-lg"
            >
              <Phone size={22} />
              Ring nu
            </a>
            <a
              href="https://www.facebook.com/paapladsen"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 w-full bg-zinc-800 text-white px-4 py-4 rounded-none font-bold text-lg"
            >
              <Facebook size={22} />
              Følg os på Facebook
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
